/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import IGlobalScope from './IGlobalScope';
import IDocument from './IDocument';
import ILocation from './ILocation';
import INetworkInformation from './INetworkInformation';
import INavigator from './INavigator';
import IWindow from './IWindow';
import IProvidedGlobalScope from './IProvidedGlobalScope';
import IDisposable from '../lang/IDisposable';
import Subject from '../rx/Subject';
import ReadOnlySubject from '../rx/ReadOnlySubject';

export class GlobalScope implements IGlobalScope {
  private static readonly _instance = new Subject<IGlobalScope | null>(null);
  private static readonly _readOnlyInstance = new ReadOnlySubject<IGlobalScope | null>(GlobalScope._instance);
  private readonly _document: IDocument;
  private readonly _location: ILocation;
  private readonly _navigator: INavigator;
  private readonly _window: IWindow;

  constructor(providedGlobalScope: IProvidedGlobalScope) {
    this._document = providedGlobalScope.document;
    this._location = providedGlobalScope.location;
    this._navigator = providedGlobalScope.navigator;
    this._window = providedGlobalScope.window;
  }

  public static setInstance(providedGlobalScope: IProvidedGlobalScope): void {
    if (this._instance.value) {
      throw new Error('GlobalScope can only be set one time');
    }

    // TODO(AZ): Validate providedGlobalScope
    this._instance.value = new GlobalScope(providedGlobalScope);
  }

  public static getInstance(): ReadOnlySubject<IGlobalScope | null> {
    return GlobalScope._readOnlyInstance;
  }

  public static subscribe(callback: (value: IGlobalScope | null) => void): IDisposable {
    return GlobalScope._readOnlyInstance.subscribe(callback);
  }

  get FormData(): typeof FormData {
    return this._window.FormData;
  }

  get HTMLVideoElement(): typeof HTMLVideoElement {
    return this._window.HTMLVideoElement;
  }

  get MediaStream(): typeof MediaStream {
    return this._window.MediaStream;
  }

  get MediaStreamTrack(): typeof MediaStreamTrack {
    return this._window.MediaStreamTrack;
  }

  get MediaSource(): typeof MediaSource {
    return this._window.MediaSource;
  }

  get RTCPeerConnection(): typeof RTCPeerConnection {
    return this._window.RTCPeerConnection;
  }

  get RTCSessionDescription(): typeof RTCSessionDescription {
    return this._window.RTCSessionDescription;
  }

  get RTCIceCandidate(): typeof RTCIceCandidate {
    return this._window.RTCIceCandidate;
  }

  get URL(): typeof URL {
    return this._window.URL;
  }

  get URLSearchParams(): typeof URLSearchParams {
    return this._window.URLSearchParams;
  }

  get addDocumentEventListener(): (type: string, listener: (event: Event) => void, options?: boolean | AddEventListenerOptions) => void {
    return this._document.addEventListener.bind(this._document);
  }

  get addWindowEventListener(): (type: string, listener: (event: Event) => void, options?: boolean | AddEventListenerOptions) => void {
    return this._window.addEventListener.bind(this._window);
  }

  get clearInterval(): (id?: number | undefined) => void {
    return this._window.clearInterval.bind(this._window);
  }

  get clearTimeout(): (id?: number | undefined) => void {
    return this._window.clearTimeout.bind(this._window);
  }

  get documentCreateElement(): <Element extends HTMLElement>(tag: string) => Element {
    return this._document.createElement.bind(this._document);
  }

  get documentHasFocus(): () => boolean {
    return this._document.hasFocus.bind(this._document);
  }

  get documentIsHidden(): boolean {
    return !!this._document.hidden;
  }

  get documentQuerySelector(): <Element extends HTMLElement>(tag: string) => Element | null {
    return this._document.querySelector.bind(this._document);
  }

  get fetch(): (input: string | URL | Request, init?: RequestInit) => Promise<Response> {
    return this._window.fetch.bind(this._window);
  }

  get locationHostname(): string {
    return this._location.hostname;
  }

  get locationSearch(): string {
    return this._location.search;
  }

  get navigatorConnection(): INetworkInformation | undefined {
    return this._navigator.connection;
  }

  get navigatorIsOnLine(): boolean {
    return this._navigator.onLine;
  }

  get pageLoadTime(): number | undefined {
    return this._window['__phenixPageLoadTime'] || this._window['__pageLoadTime'];
  }

  get queueMicrotask(): (microtask: () => void) => void {
    return this._window.queueMicrotask.bind(this._window);
  }

  get removeDocumentEventListener(): (type: string, listener: (event: Event) => void, options?: boolean | AddEventListenerOptions) => void {
    return this._document.removeEventListener.bind(this._document);
  }

  get requestAnimationFrame(): (callback: FrameRequestCallback) => number {
    return this._window.requestAnimationFrame.bind(this._window);
  }

  get removeWindowEventListener(): (type: string, listener: (event: Event) => void, options?: boolean | AddEventListenerOptions) => void {
    return this._window.removeEventListener.bind(this._window);
  }

  get setInterval(): (handler: string | Function, timeout?: number | undefined) => number {
    return this._window.setInterval.bind(this._window);
  }

  get setTimeout(): (handler: string | Function, timeout?: number | undefined) => number {
    return this._window.setTimeout.bind(this._window);
  }
}

export default GlobalScope.getInstance();